import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sparkles, TrendingUp, Users } from 'lucide-react';
import { useCountdown } from './useCountdown';

/* ─── Count-up animation for the pot total ────────────────────── */
function useCountUp(target, duration = 1400) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!target) {
      setValue(0);
      return;
    }
    const start = Date.now();

    const step = () => {
      const elapsed = Date.now() - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };

    requestAnimationFrame(step);
  }, [target, duration]);

  return value;
}

/* ─── Loading skeleton ────────────────────────────────────────── */
const BannerSkeleton = () => (
  <div className="animate-pulse rounded-xl border border-border/40 bg-card/20 px-4 sm:px-6 py-10 md:py-14">
    <div className="flex flex-col lg:flex-row gap-8 items-start lg:items-center">
      <div className="flex-1 space-y-4">
        <div className="h-3 bg-muted rounded w-40" />
        <div className="h-7 bg-muted rounded w-64" />
        <div className="h-4 bg-muted rounded w-96 max-w-full" />
        <div className="h-11 bg-muted rounded-full w-44" />
      </div>
      <div className="space-y-3 shrink-0">
        <div className="h-3 bg-muted rounded w-44" />
        <div className="h-12 bg-muted rounded w-52" />
        <div className="h-3 bg-muted rounded w-32" />
      </div>
    </div>
  </div>
);

/* ─── Countdown segment ──────────────────────────────────────── */
const CountSegment = ({ value, label }) => (
  <div className="flex items-baseline gap-0.5">
    <span className="text-2xl md:text-3xl font-semibold tabular-nums text-foreground">
      {String(value).padStart(2, '0')}
    </span>
    <span className="text-[10px] uppercase tracking-widest text-muted-foreground ml-0.5">
      {label}
    </span>
  </div>
);

/* ─── Pot balance + countdown — shared between states ─────────── */
const PotBlock = ({ animatedPot, countdown, endsAt }) => (
  <div className="flex flex-col items-start lg:items-end gap-5 shrink-0">
    <div className="space-y-1.5 text-left lg:text-right">
      <p className="text-xs uppercase tracking-widest text-muted-foreground">Community Fund this month</p>
      <div className="relative inline-block">
        <div
          className="absolute inset-0 rounded-full blur-2xl bg-amber-500/25 scale-150 pointer-events-none"
          aria-hidden
        />
        <p className="relative text-4xl md:text-5xl font-semibold text-amber-400 tabular-nums">
          🍕 {animatedPot.toLocaleString()}
        </p>
      </div>
      <p className="text-sm text-muted-foreground">slices to allocate together</p>
    </div>

    {endsAt && (
      <div className="space-y-1.5 text-left lg:text-right">
        <p className="text-xs uppercase tracking-widest text-muted-foreground">Month ends in</p>
        <div className="flex items-center gap-2.5 justify-start lg:justify-end">
          <CountSegment value={countdown.days} label="d" />
          <CountSegment value={countdown.hours} label="h" />
          <CountSegment value={countdown.mins} label="m" />
          <CountSegment value={countdown.secs} label="s" />
        </div>
      </div>
    )}
  </div>
);

/* ─── No nominees yet ─────────────────────────────────────────── */
const EmptyFundCard = ({ animatedPot, countdown, endsAt }) => (
  <div className="relative overflow-hidden rounded-xl border border-amber-500/20 bg-amber-500/[0.04]">
    <div className="pointer-events-none absolute right-0 top-0 h-full w-1/2" aria-hidden>
      <div className="absolute right-8 top-1/2 -translate-y-1/2 w-[400px] h-[400px] rounded-full bg-amber-500/15 blur-[100px]" />
    </div>

    <div className="relative px-4 sm:px-6 py-10 md:py-14">
      <div className="flex flex-col lg:flex-row gap-8 lg:gap-16 items-start lg:items-center">
        <div className="flex-1 space-y-3 min-w-0">
          <p className="text-xs uppercase tracking-widest text-amber-500 font-medium">
            🍕 Community Fund
          </p>
          <h2 className="text-xl font-semibold text-foreground">
            Nominate a work for this month's Community Fund.
          </h2>
          <p className="text-sm text-muted-foreground max-w-md leading-relaxed">
            The fund holds{' '}
            <span className="text-amber-400 font-medium tabular-nums">
              {animatedPot.toLocaleString()} 🍕
            </span>{' '}
            and no works have been nominated yet. Find a film or series you believe
            in and put it forward — at month end the whole pot goes to the work
            the community backs most.
          </p>
          <div className="pt-2">
            <Button
              asChild
              className="h-11 px-6 rounded-full text-sm font-medium bg-amber-500 hover:bg-amber-600 text-white border-0 shadow-lg shadow-amber-500/20"
            >
              <Link to="/browse">
                <Sparkles className="mr-2 h-4 w-4" />
                Browse works to nominate
              </Link>
            </Button>
          </div>
        </div>

        <PotBlock animatedPot={animatedPot} countdown={countdown} endsAt={endsAt} />
      </div>
    </div>
  </div>
);

/* ─── CommunityPickBanner ─────────────────────────────────────── */
/**
 * Home banner for the monthly works-centered Community Fund.
 *
 * Props:
 *   worksSummary — from useSlicePot().worksSummary
 *     { pot_balance, period: { id, starts_at, ends_at } | null,
 *       leaderboard: [{ work_id, title, slug, poster_url, publisher, votes, voters, nominations }] }
 *   isLoading    — boolean
 */
const CommunityPickBanner = ({ worksSummary, isLoading }) => {
  const leaderboard = worksSummary?.leaderboard ?? [];
  const potBalance  = worksSummary?.pot_balance ?? 0;
  const endsAt      = worksSummary?.period?.ends_at ?? null;

  const countdown   = useCountdown(endsAt);
  const animatedPot = useCountUp(potBalance);

  /* ── Loading ── */
  if (isLoading) return <BannerSkeleton />;

  /* ── No nominees yet ── */
  if (leaderboard.length === 0) {
    return (
      <EmptyFundCard
        animatedPot={animatedPot}
        countdown={countdown}
        endsAt={endsAt}
      />
    );
  }

  const leadWork = leaderboard[0];
  const hasVotes = (leadWork?.votes ?? 0) > 0;

  return (
    <div className="relative overflow-hidden rounded-xl border border-border/40 bg-card/20">
      {/* Background work poster */}
      {leadWork.poster_url && (
        <div className="absolute inset-0 pointer-events-none" aria-hidden>
          <img
            src={leadWork.poster_url}
            alt=""
            className="w-full h-full object-cover opacity-[0.15]"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/85 to-background/50" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
        </div>
      )}

      {/* Warm ambient glow */}
      <div className="pointer-events-none absolute right-0 top-0 h-full w-1/2" aria-hidden>
        <div className="absolute right-8 top-1/2 -translate-y-1/2 w-[480px] h-[480px] rounded-full bg-amber-500/20 blur-[120px]" />
      </div>

      <div className="relative px-4 sm:px-6 py-10 md:py-14">
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-16 items-start lg:items-center">

          {/* Left: leading work info */}
          <div className="flex-1 space-y-4 min-w-0">

            <p className="text-xs uppercase tracking-widest text-amber-500 font-medium">
              🍕 Community Fund
              {hasVotes ? ' — Leading this month' : ''}
            </p>

            <h2 className="text-2xl sm:text-3xl font-semibold text-foreground leading-snug max-w-xl">
              {leadWork.title}
            </h2>

            {leadWork.publisher?.company_name && (
              <p className="text-sm text-muted-foreground">
                by{' '}
                <span className="text-foreground">
                  {leadWork.publisher.company_name}
                </span>
              </p>
            )}

            {!hasVotes && (
              <p className="text-sm text-muted-foreground max-w-md leading-relaxed">
                No votes yet this month — be the first to direct the Community Fund.
              </p>
            )}

            {hasVotes && (
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <TrendingUp className="h-3.5 w-3.5 text-amber-400 shrink-0" />
                  <span className="text-amber-400 font-semibold tabular-nums">
                    {leadWork.votes.toLocaleString()}
                  </span>
                  <span>slice votes</span>
                </div>
                <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <Users className="h-3.5 w-3.5 shrink-0" />
                  <span>{leadWork.voters.toLocaleString()} voters</span>
                </div>
              </div>
            )}

            {/* CTAs */}
            <div className="flex flex-wrap items-center gap-3 pt-1">
              <Button
                asChild
                className="h-11 px-6 rounded-full text-sm font-medium bg-amber-500 hover:bg-amber-600 text-white border-0 shadow-lg shadow-amber-500/20"
              >
                <Link to="/fund">
                  <Sparkles className="mr-2 h-4 w-4" />
                  View the fund
                </Link>
              </Button>
              {leadWork.slug && (
                <Link
                  to={`/watch/${leadWork.slug}`}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  View work →
                </Link>
              )}
            </div>
          </div>

          {/* Right: pot balance + countdown */}
          <PotBlock animatedPot={animatedPot} countdown={countdown} endsAt={endsAt} />
        </div>
      </div>
    </div>
  );
};

export default CommunityPickBanner;
